import React from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';

// components
import {
  Element,
  FontType,
  Color,
  FlipContent,
  useFlip,
  TogglePosition,
  Typography
} from '@americanexpress/aqx-components';
// selectors
import { getPageData } from 'src/universal/redux/page';
import { sendDTMClickEvent } from 'service_modules/models/dtm-tagging';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';

// etc
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';

import styles from './__styles__/index.scss?modules';

const Front = ({ text, header, assets }) => {
  const assetObj = parseArrayToObject(assets, 'breakpoint');

  return (
    <Element className={styles.contentContainer}>
      <Element className={styles.textContainer}>
        <Typography
          $size="20px"
          $lineHeight="28px"
          $family={FontType.BentonBold}
          theme={{
            color: Color.Gray06,
            marginBottom: 10
          }}
          className={styles.header}
          Component="h3"
        >
          {header}
        </Typography>
        <Typography
          $size="15px"
          $lineHeight="22px"
          $family={FontType.HelveticaRegular}
          theme={{
            color: Color.Gray06
          }}
        >
          {text}
        </Typography>
      </Element>
      <LazyScrollMetricTracker>
        <Element className={styles.imgContainer}>
          <Element
            className={styles.desktopImg}
            Component="img"
            src={assetObj?.desktop?.url}
            alt={assetObj?.desktop?.alt}
          />
          <Element
            className={styles.mobileImg}
            Component="img"
            src={assetObj?.mobile?.url}
            alt={assetObj?.mobile?.alt}
          />
        </Element>
      </LazyScrollMetricTracker>
    </Element>
  );
};

const Back = ({ title, backText, backTextList = [] }) => {
  return (
    <Element className={styles.contentContainer}>
      <Element className={styles.textContainer}>
        <Typography
          Component="h3"
          $size="20px"
          $lineHeight="28px"
          $family={FontType.BentonMedium}
          theme={{ color: Color.Black, marginBottom: 10 }}
          className={styles.header}
        >
          {title}
        </Typography>
        <Element>
          {backText && (
            <Typography
              $size="15px"
              $lineHeight="22px"
              $family={FontType.HelveticaRegular}
              theme={{ color: Color.Gray06 }}
              className={styles.backText}
            >
              {backText}
            </Typography>
          )}
          <Element Component="ul" className={styles.backTextList}>
            {backTextList.map((eachItem, index) => (
              <Element Component="li" key={index}>
                {eachItem.header && (
                  <Typography
                    $size="15px"
                    $lineHeight="22px"
                    $family={FontType.HelveticaBold}
                    theme={{ color: Color.Gray06 }}
                    className={styles.backText}
                  >
                    {eachItem.header}
                  </Typography>
                )}
                <Typography
                  $size="15px"
                  $lineHeight="22px"
                  $family={FontType.HelveticaRegular}
                  theme={{ color: Color.Gray06, marginBottom: 20 }}
                  className={styles.backText}
                >
                  {eachItem.content}
                </Typography>
              </Element>
            ))}
          </Element>
        </Element>
      </Element>
    </Element>
  );
};

const Tile = ({ content }) => {
  const { isFlipped, setIsFlipped, rotation, setRotation } = useFlip();

  const dtmObj = parseArrayToObject(content.dtmEvents, 'tag');

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    setRotation(r => r + 180);
    if (!isFlipped) {
      sendDTMClickEvent(dtmObj.clickToBack);
    } else {
      sendDTMClickEvent(dtmObj.clickToFront);
    }
  };

  return (
    <Element className={clsx(styles.flipCardContainer)}>
      <FlipContent
        isFlipped={isFlipped}
        handleFlip={handleFlip}
        rotation={rotation}
        togglePosition={TogglePosition.TopRight}
        ariaLabelFront={content.frontAriaLabel}
        ariaLabelBack={content.backAriaLabel}
        cardStyles={{ back: { display: 'flex' }, front: { display: 'flex' } }}
      >
        <Front {...content} />
        <Back {...content} />
      </FlipContent>
    </Element>
  );
};

const Tiles = ({ data }) => {
  if (!data) {
    return null;
  }
  const { content, header, dtm } = data;

  return (
    <React.Fragment>
      <LazyScrollMetricTracker scrollDtm={dtm} />
      <Element className={styles.tileSection}>
        <Element className={styles.headerBox}>
          <Typography
            Component="h2"
            $family={FontType.BentonLight}
            $size={'30px'}
            $lineHeight={'38px'}
            theme={{ color: Color.Gray06 }}
          >
            {header}
          </Typography>
        </Element>

        <Element className={styles.flipCardsContainer}>
          {content.map((item, i) => <Tile key={i} content={item} />)}
        </Element>
      </Element>
    </React.Fragment>
  );
};

const mapStateToProps = state => ({
  data: getPageData(state).corpVacTiles
});

export default connect(mapStateToProps)(Tiles);
